/*
 * decaffeinate suggestions:
 * DS103: Rewrite code to no longer use __guard__
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import _ from 'underscore';

import {AnalyticsEventMetadata} from 'analytics/AnalyticsTypes';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import {setLocationHref} from 'BenchlingUtil';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {EventConstants} from 'constants/EventConstants';
import {SavingState, TagSchema} from 'constants/RegistryConstants';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FileStates from 'FileStates';
import {SchemaTag} from 'folder-items/FileConstants';
import {
  readFragmentFromCache,
  writeFragmentsToCache,
  writeFragmentToCache,
} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import AppDispatcher from 'lib/Dispatcher';
import navigateToURL from 'lib/navigateToURL';
import {NameTemplatePart} from 'name-templates/NameTemplateConstants';
import notifications from 'notifications';
import RegistryActions from 'registry/RegistryActions';
import * as RegistryUtil from 'registry/RegistryUtil';
import {
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import {SchemaFieldEdge, SchemaFieldPath} from 'schemas/fields/SchemaFieldConstants';
import {StarsResponse} from 'stars/StarsConstants';
import {setTagSchemaSavingState} from 'tag-schema/TagSchemaClientState';

import {StarredTagSchemaIdsGraphQL} from '../../graphql-fragments/schemas/tag-schemas/__generated/StarredTagSchemaIdsGraphQL';
import {TagSchemaGraphQL} from '../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaGraphQL';
import * as TagSchemaDAO from './TagSchemaDAO';

export function delete_(tagSchemaId: string): Promise<void> {
  return TagSchemaDAO.delete_(tagSchemaId)
    .then(() => {
      notifications.success('Schema deleted.');
    })
    .catch((response) => {
      notifications.notifyError(response);
    });
}

function _createWithXHR(properties: TagSchemaDAO.CreateTagSchemaParams): JQueryPromise<TagSchemaGraphQL> {
  setTagSchemaSavingState(SavingState.PENDING);
  return TagSchemaDAO.create(properties)
    .done((response: TagSchemaGraphQL) => {
      writeFragmentToCache(getFragmentWrapper('TagSchema'), response);
      setTagSchemaSavingState(SavingState.CREATE_SUCCESS);
      notifications.success('Created schema successfully.', {
        actionText: 'Open',
        duration: 10,
        onActionClick: async () => {
          // Currently, we have to construct the schema URL by using the `registryId` and fetching the
          // `ownerUrlId`. Ideally, the server response would have the URL (`navigateToURL(response.url)`),
          // or the schema URL would simply only require the `tagSchemaId`
          // (`navigateToURL(RegistryUtil.getTagSchemaSettingsUrl(response.id))`).
          const ownerUrlId = await RegistryActions.getOwnerUrlId(response.registry.id, {
            loadingMessage: 'Opening schema...',
          });
          const url = RegistryUtil.getTagSchemaSettingsUrl(ownerUrlId, response.registry.id, response.id);
          navigateToURL(url);
        },
      });
    })
    .fail((response) => {
      setTagSchemaSavingState(SavingState.FAILURE);
      // Callers like `TagSchemaCreateFlow` depend on this functionality (that `_createWithXHR` displays the
      // error notification if the server call to create the `TagSchema` failed).
      notifications.notifyError(response);
    });
}

export function create(properties: TagSchemaDAO.CreateTagSchemaParams): JQueryPromise<TagSchemaGraphQL> {
  return _createWithXHR(properties);
}

export function duplicateTagSchema(
  tagSchemaId: string,
  params: TagSchemaDAO.DuplicateTagSchemaParams,
  analyticsMetadata?: AnalyticsEventMetadata
): Promise<TagSchemaGraphQL | null> {
  return TagSchemaDAO.duplicate(tagSchemaId, params, analyticsMetadata)
    .then((response) => {
      writeFragmentToCache(getFragmentWrapper('TagSchema'), response);
      notifications.success('Duplicated schema successfully.', {
        actionText: 'Open',
        duration: 10,
        onActionClick: async () => {
          const ownerUrlId = await RegistryActions.getOwnerUrlId(response.registry.id, {
            loadingMessage: 'Opening schema...',
          });
          const url = RegistryUtil.getTagSchemaSettingsUrl(ownerUrlId, response.registry.id, response.id);
          navigateToURL(url);
        },
      });
      return response;
    })
    .catch((error) => {
      notifications.notifyError(error);
      return null;
    });
}

function _updateWithXHR(
  tagSchemaId: string,
  properties: TagSchemaDAO.UpdateTagSchemaParams
): JQueryPromise<TagSchemaDAO.UpdateTagSchemaResponse> {
  setTagSchemaSavingState(SavingState.PENDING);
  return TagSchemaDAO.update(tagSchemaId, properties)
    .done((response: TagSchemaDAO.UpdateTagSchemaResponse) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.TAG_SCHEMA_UPDATED,
        tagSchema: response.tagSchema,
      });
      setTagSchemaSavingState(SavingState.SUCCESS);
      notifications.success('Updated schema successfully.');

      // Refresh open files and folders showing files with the tag schema.
      //
      // 1. Files
      // eslint-disable-next-line no-restricted-syntax
      const fileIdsWithTagSchema = _.pluck(
        FileStates.get().filter(
          // oxlint-disable-next-line @typescript-eslint/no-explicit-any
          (fileState: any) =>
            __guard__(
              typeof fileState.model === 'function' ? fileState.model().tagSchema() : undefined,
              (x: TagSchema) => x.id
            ) === tagSchemaId
        ),
        'id'
      );
      FileBrowserDAO.bulkGetTags(fileIdsWithTagSchema).done(
        (bulkGetTagsResponse: {data: Array<{id: string; tags: Array<SchemaTag>}>}) => {
          // eslint-disable-next-line no-restricted-syntax
          _.map(bulkGetTagsResponse.data, ({id, tags}) => {
            AppDispatcher.dispatch({
              actionType: EventConstants.FILE_TAG_SCHEMA_UPDATED,
              fileId: id,
              tags,
              tagSchema: response.tagSchema,
            });
          });
        }
      );
      // 2. Folders
      FolderDAOCache.invalidateAllFolders();
    })
    .fail((response) => {
      notifications.notifyError(response, {duration: 8}); // Some validation errors are pretty long
      setTagSchemaSavingState(SavingState.FAILURE);
    });
}

export function update(
  tagSchemaId: string,
  properties: TagSchemaDAO.UpdateTagSchemaParams
): JQueryPromise<TagSchemaDAO.UpdateTagSchemaResponse> {
  return _updateWithXHR(tagSchemaId, properties);
}

export function resetSavingState(): void {
  setTagSchemaSavingState(SavingState.COMPLETED);
}

export function setNameTemplateParts(
  tagSchemaId: string,
  nameTemplateParts: Array<NameTemplatePart>,
  shouldOrderNamePartsBySequence: boolean
): Promise<void> {
  setTagSchemaSavingState(SavingState.PENDING);
  return TagSchemaDAO.setNameTemplateParts(tagSchemaId, nameTemplateParts, shouldOrderNamePartsBySequence)
    .then(() => {
      setTagSchemaSavingState(SavingState.SUCCESS);
      notifications.success('Updated name template.');
    })
    .catch((response) => {
      notifications.notifyError(response);
      setTagSchemaSavingState(SavingState.FAILURE);
    });
}

export function setContainerNameTemplateParts(
  tagSchemaId: string,
  nameTemplateParts: Array<NameTemplatePart>,
  dataModsHeader?: DataModificationsHeader
): Promise<TagSchemaDAO.SetContainerNameTemplateResponse> {
  setTagSchemaSavingState(SavingState.PENDING);
  return TagSchemaDAO.setContainerNameTemplateParts(tagSchemaId, nameTemplateParts, dataModsHeader)
    .then((response: TagSchemaDAO.SetContainerNameTemplateResponse) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.TAG_SCHEMA_UPDATED,
        tagSchema: response.tagSchema,
      });
      setTagSchemaSavingState(SavingState.SUCCESS);
      notifications.success('Updated container name template.');
      return response;
    })
    .catch((response) => {
      notifications.notifyError(response);
      setTagSchemaSavingState(SavingState.FAILURE);
      return {tagSchema: null, nameTemplate: null};
    });
}

/**
 * @return Promise resolving `true` if the request to change the name template is submitted, `false` if not.
 *         The request may not be submitted if data mods are enabled and the user clicks the "x" to close the
 *         data mods modal.
 */
export async function setContainerNameTemplatePartsWithDataMods(
  tagSchemaId: string,
  nameTemplateParts: Array<NameTemplatePart>,
  onSuccessCallback?: (value: TagSchemaDAO.SetContainerNameTemplateResponse) => void
): Promise<boolean> {
  const changeSummary = getInventoryRegistryChangeSummary({
    apiIds: [tagSchemaId],
    action: InventoryRegistryDataModAction.MODIFY,
  });
  return await callAsyncWithDataModifications({
    changeSummary,
    onPerformAction: (dataModsHeader?: DataModificationsHeader) => {
      setContainerNameTemplateParts(tagSchemaId, nameTemplateParts, dataModsHeader).then(onSuccessCallback);
    },
    apiIds: [tagSchemaId],
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin: ActionOrigin.SETTINGS_ENTITY_SCHEMA_PAGE,
      modifiedAction: InventoryRegistryDataModAction.MODIFY,
      numModifiedTotalItems: 1,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY,
    // Hard code useDataModsIfEnabledAnywhere to true to
    // enable data mods when project level data mods are enabled
    useDataModsIfEnabledAnywhere: true,
  });
}

export function fetchContainerNameTemplate(
  tagSchemaId: string
): Promise<TagSchemaDAO.FetchContainerNameTemplateResponse> {
  return TagSchemaDAO.fetchContainerNameTemplate(tagSchemaId)
    .then((response) => response)
    .catch((response) => {
      notifications.notifyError(response);
      return {nameTemplate: null};
    });
}

export function setHoistedFields(
  tagSchemaId: string,
  hoistedTagSchemaGraphs: Array<SchemaFieldPath | TagSchemaDAO.ClientSchemaFieldPath>,
  onSuccessCallback?: () => void
): void {
  setTagSchemaSavingState(SavingState.PENDING);
  TagSchemaDAO.setHoistedFields(tagSchemaId, hoistedTagSchemaGraphs)
    .done((response: TagSchemaDAO.SetHoistedFieldsResponse) => {
      setTagSchemaSavingState(SavingState.COMPLETED);
      notifications.success('Successfully modified hoisted fields.');
      onSuccessCallback?.();
    })
    .fail((response) => {
      notifications.notifyError(response);
      setTagSchemaSavingState(SavingState.FAILURE);
    });
}

export function exportSpreadsheetTemplate(tagSchemaId: string): void {
  const notificationId = _.uniqueId('export-spreadsheet-template-');
  notifications.info('Exporting...', {id: notificationId, duration: 0});
  TagSchemaDAO.exportSpreadsheetTemplate(tagSchemaId)
    .then((response: TagSchemaDAO.ExportSpreadsheetTemplateResponse) => {
      setLocationHref(response.url);
      notifications.success('Export complete. Downloading...', {id: notificationId});
    })
    .catch((response: TagSchemaDAO.ExportSpreadsheetTemplateResponse) => {
      notifications.close(notificationId);
      notifications.notifyError(response, {id: notificationId});
    });
}

export async function updatedRequiredLinkEntitySchemaFieldPath(
  fieldId: string,
  schemaFieldEdges: Array<SchemaFieldEdge> | null
): Promise<void> {
  const response = await TagSchemaDAO.updatedRequiredLinkEntitySchemaFieldPath(fieldId, schemaFieldEdges);
  AppDispatcher.dispatch({
    actionType: EventConstants.TAG_SCHEMA_UPDATED,
    tagSchema: response.tagSchema,
  });
  setTagSchemaSavingState(SavingState.SUCCESS);
  notifications.success('Updated parent lineage enforcement.');
}

function __guard__<T, R>(value: T, transform: (val: T) => R): R | undefined {
  return value != null ? transform(value) : undefined;
}

export async function updateTagSchemaCollaborators(
  tagSchemaId: string,
  data: TagSchemaDAO.UpdateSchemaCollaboratorsRequest
): Promise<void> {
  const response = await TagSchemaDAO.updateCollaborators(tagSchemaId, data);
  writeFragmentsToCache(getFragmentWrapper('CollaboratorInfo'), [response.tagSchema]);
}

type StarredTagSchema = StarredTagSchemaIdsGraphQL['starredTagSchemas'][number];

export function toggleTagSchemaStar(tagSchemaId: string): Promise<void> {
  const result = readFragmentFromCache(getFragmentWrapper('StarredTagSchemaIds')) ?? {
    starredTagSchemas: [],
  };
  const starredTagSchemaIds = result.starredTagSchemas.map(({id}) => id);
  if (starredTagSchemaIds.includes(tagSchemaId)) {
    return TagSchemaDAO.unstar([tagSchemaId]).then((resp: StarsResponse) => {
      writeFragmentToCache(getFragmentWrapper('StarredTagSchemaIds'), {
        __typename: 'Query',
        starredTagSchemas: resp.starred.map(
          (id) => ({__typename: 'TagSchemaNode', id}) satisfies StarredTagSchema
        ),
      });
    });
  } else {
    return TagSchemaDAO.star([tagSchemaId]).then((resp: StarsResponse) => {
      writeFragmentToCache(getFragmentWrapper('StarredTagSchemaIds'), {
        __typename: 'Query',
        starredTagSchemas: resp.starred.map(
          (id) => ({__typename: 'TagSchemaNode', id}) satisfies StarredTagSchema
        ),
      });
    });
  }
}
